import { notifications } from '@mantine/notifications';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EventForm } from 'src/Components/Forms/Event/EventForm';
import { RoomConflict } from 'src/Components/Forms/Event/RoomSelector/RoomSelector.types';
import { useCreateEventForm } from 'src/Hooks/Forms/UseCreateEventForm';
import { useCreateEvent } from 'src/Hooks/Mutations/useCreateEvent';
import useFetchHouseholds from 'src/Hooks/Queries/useFetchHouseholds';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';

import { Props } from './EventCreationFormWrapper.types';

const EVENT_ROOM_CONFLICT_CODE = 'EVENT_ROOM_CONFLICT';

interface RoomConflictErrorBody {
  code?: string;
  conflicts?: RoomConflict[];
}

/** Extracts the structured 409 payload the back emits for a room overlap, or null for anything else. */
const extractRoomConflicts = (error: unknown): RoomConflict[] | null => {
  const axiosError = error as AxiosError<RoomConflictErrorBody> | undefined;
  const body = axiosError?.response?.data;
  if (body?.code !== EVENT_ROOM_CONFLICT_CODE) return null;
  return body.conflicts ?? [];
};

const EventCreationFormWrapper = ({
  onCreate,
  onCancel,
  isReservation,
  defaultDate,
  defaultHouseholdId,
}: Props) => {
  const form = useCreateEventForm({ isReservation, defaultDate, defaultHouseholdId });
  const { t } = useTranslation();
  const [roomConflicts, setRoomConflicts] = useState<RoomConflict[] | undefined>(undefined);
  const { mutate } = useCreateEvent({
    onSuccess: () => {
      setRoomConflicts(undefined);
      onCreate();
    },
    onError: error => {
      const conflicts = extractRoomConflicts(error);
      if (conflicts) {
        setRoomConflicts(conflicts);
        return;
      }
      setRoomConflicts(undefined);
      notifications.show({
        color: 'red',
        message: t(
          'createEventForm.errors.submitFailed',
          'L’événement n’a pas pu être enregistré. Vérifiez votre connexion et réessayez.'
        ),
      });
    },
  });
  const { data: currentUser } = useFetchUser();
  const { data: households } = useFetchHouseholds();
  const hasPrefilledParticipants = useRef(false);

  // Prefill once: the current user, plus the members of the household the drawer was opened from.
  useEffect(() => {
    if (hasPrefilledParticipants.current || !currentUser) return;
    if (defaultHouseholdId && !households) return;

    const defaultHouseholdMemberIds =
      households
        ?.find(household => household.id === defaultHouseholdId)
        ?.members.map(member => member.userId)
        .filter((id): id is string => !!id) ?? [];

    hasPrefilledParticipants.current = true;
    form.setFieldValue('participantIds', [
      ...new Set([...form.values.participantIds, currentUser.id, ...defaultHouseholdMemberIds]),
    ]);
  }, [currentUser, households, defaultHouseholdId]);

  return (
    <EventForm
      onCancel={onCancel}
      form={form}
      onSubmit={form.onSubmit(values => {
        setRoomConflicts(undefined);
        mutate(values);
      })}
      submitButtonLabel={t('createEventForm.addEvent', 'Ajouter')}
      isReservation={isReservation}
      roomConflicts={roomConflicts}
    />
  );
};

export default EventCreationFormWrapper;
